import { toast } from "sonner";

export const useClipboard = () => {
  // Fallback copy method using textarea (for when Clipboard API is blocked)
  const copyToClipboardFallback = (text: string): boolean => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.top = "0";
      textarea.style.left = "0";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);

      return successful;
    } catch (err) {
      console.error("Fallback copy failed:", err);
      return false;
    }
  };

  const copyToClipboard = async (text: string, successMessage?: string) => {
    let success = false;

    try {
      // Try modern Clipboard API first
      await navigator.clipboard.writeText(text);
      success = true;
    } catch (err) {
      console.log("Clipboard API failed, trying fallback method:", err);
      // Use fallback method
      success = copyToClipboardFallback(text);
    }

    if (success) {
      toast.success("Copied!", {
        description: successMessage || "Text copied to clipboard",
      });
    } else {
      toast.error("Copy Failed", {
        description: "Failed to copy to clipboard",
      });
    }

    return success;
  };

  const copyHtmlToClipboard = async (html: string, plainText: string, successMessage?: string) => {
    let success = false;
    try {
      if (!navigator.clipboard.write) {
        throw new Error("Clipboard API does not support write.");
      }
      const htmlBlob = new Blob([html], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });
      const clipboardItem = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });
      await navigator.clipboard.write([clipboardItem]);
      success = true;
    } catch (err) {
      console.log("Clipboard API with HTML failed, trying fallback method with plain text:", err);
      success = copyToClipboardFallback(plainText);
    }

    if (success) {
      toast.success("Copied!", {
        description: successMessage || "Text copied to clipboard",
      });
    } else {
      toast.error("Copy Failed", {
        description: "Failed to copy to clipboard",
      });
    }

    return success;
  };

  return { copyToClipboard, copyHtmlToClipboard };
};
